import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Plus, Save, X, Copy, ExternalLink, GripVertical } from 'lucide-react';
import { specialEventAPI, trekAPI, contentAPI, adminAPI } from '../services/api';
import { specialEventPublicUrl } from '../utils/specialEventPublicUrl.js';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { PageHeader } from '../components/ui';
import MultiImageUpload from '../components/MultiImageUpload';
import './ModuleAdmin.css';
import { useNavigate, useParams } from 'react-router-dom';
import AdminBackLink from '../components/AdminBackLink';
import Sortable from 'sortablejs';

const emptyForm = {
  name: '',
  tagline: '',
  slug: '',
  status: 'active',
  videoId: '',
  isHighlighted: false,
  isActive: true
};

const SortableList = ({ items, onReorder, renderItem, listClass = '', container: Container = 'div' }) => {
  const listRef = useRef(null);

  useEffect(() => {
    if (!listRef.current) return;
    const sortable = Sortable.create(listRef.current, {
      animation: 150,
      handle: '.drag-handle',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        onReorder(evt.oldIndex, evt.newIndex);
      }
    });
    return () => sortable.destroy();
  }, [onReorder]);

  return (
    <Container ref={listRef} className={listClass}>
      {items.map(renderItem)}
    </Container>
  );
};

const SpecialEventForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id && id !== 'new');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editing] = useState(isEditing ? id : null);
  const [selectedTrekIds, setSelectedTrekIds] = useState([]);
  const [selectedStories, setSelectedStories] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [trekSearch, setTrekSearch] = useState('');
  const [storySearch, setStorySearch] = useState('');
  
  const { permissions } = useAuth();
  const { showToast } = useToast();
  const canEditSlugs = permissions.includes('*') || permissions.includes('slug_manage');
  
  const { data: trekRes } = useQuery(['treks', 'for-special'], () => trekAPI.list({ isActive: true }));
  const treks = useMemo(() => trekRes?.data?.data || [], [trekRes]);
  const { data: blogsRes } = useQuery(['content', 'blogs'], () => contentAPI.getBlogs());
  const blogs = useMemo(() => {
    const raw = blogsRes?.data?.data || blogsRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [blogsRes]);

  const { data: specialDetailRes } = useQuery(
    ['special-event', editing],
    () => specialEventAPI.get(editing),
    { enabled: !!editing }
  );
  const specialDetail = specialDetailRes?.data?.data;

  useEffect(() => {
    if (editing && specialDetail) {
      setForm({
        name: specialDetail.name || '',
        tagline: specialDetail.tagline || '',
        slug: specialDetail.slug || '',
        status: specialDetail.status || 'active',
        videoId: specialDetail.videoId || '',
        isHighlighted: !!specialDetail.isHighlighted,
        isActive: !!specialDetail.isActive
      });
      if (specialDetail.enabledTreks) setSelectedTrekIds(specialDetail.enabledTreks.map((t) => t.id));
      if (specialDetail.stories) {
        setSelectedStories(
          specialDetail.stories.map((s) => ({
            id: s.id,
            blogId: s.blogId,
            title: s.title || s.blog?.title || '',
            slug: s.slug || s.blog?.slug || ''
          }))
        );
      }
      if (specialDetail.images) setSelectedImages(specialDetail.images.map((i) => i.url));
      if (specialDetail.videos) {
        setSelectedVideos(
          specialDetail.videos.map((v) => ({
            id: v.id,
            videoId: v.videoId,
            title: v.title || ''
          }))
        );
      }
    }
  }, [editing, specialDetail]);

  const createMutation = useMutation(
    async ({ form: payload, trekIds, stories, images, videos }) => {
      const res = await specialEventAPI.create(payload);
      const newId = res?.data?.data?.id;
      if (newId && trekIds?.length) await specialEventAPI.setEnabledTreks(newId, trekIds);
      if (newId && stories?.length) {
        for (let i = 0; i < stories.length; i++) {
          const s = stories[i];
          await specialEventAPI.createStory(newId, { blogId: s.blogId, title: s.title, slug: s.slug, order: i });
        }
      }
      if (newId && images?.length) {
        for (let i = 0; i < images.length; i++) {
          await specialEventAPI.createImage(newId, { url: images[i], order: i });
        }
      }
      if (newId && videos?.length) {
        for (let i = 0; i < videos.length; i++) {
          await specialEventAPI.createVideo(newId, { videoId: videos[i].videoId, title: videos[i].title, order: i });
        }
      }
      return res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['special-events']);
        navigate('/special-events');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => specialEventAPI.update(id, payload),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['special-events']);
        queryClient.invalidateQueries(['special-event', id]);
      }
    }
  );

  const deleteMutation = useMutation((id) => specialEventAPI.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['special-events']);
      navigate('/special-events');
    }
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await updateMutation.mutateAsync({ id: editing, payload: form });
      await specialEventAPI.setEnabledTreks(editing, selectedTrekIds);
      
      const existingStories = specialDetail?.stories || [];
      for (const s of existingStories) {
        if (!selectedStories.some((m) => m.blogId === s.blogId)) {
          await specialEventAPI.deleteStory(editing, s.id);
        }
      }
      for (let i = 0; i < selectedStories.length; i++) {
        const s = selectedStories[i];
        if (!existingStories.some((e) => e.blogId === s.blogId)) {
          await specialEventAPI.createStory(editing, { blogId: s.blogId, title: s.title, slug: s.slug, order: i });
        } else {
          // If update order API exists, call it. For now, we assume deleting and recreating or that the API handles order updates.
          // Since the existing code didn't update story order, we will just delete and recreate to enforce order if needed, 
          // or ideally the backend should update order. For now, recreate if order changed.
          // Actually, let's just delete all and recreate all to be safe and enforce order!
          const existing = existingStories.find(e => e.blogId === s.blogId);
          if (existing) {
             await specialEventAPI.deleteStory(editing, existing.id);
          }
          await specialEventAPI.createStory(editing, { blogId: s.blogId, title: s.title, slug: s.slug, order: i });
        }
      }

      const existingImages = specialDetail?.images || [];
      for (const img of existingImages) await specialEventAPI.deleteImage(editing, img.id);
      for (let i = 0; i < selectedImages.length; i++) await specialEventAPI.createImage(editing, { url: selectedImages[i], order: i });

      const existingVideos = specialDetail?.videos || [];
      for (const vid of existingVideos) await specialEventAPI.deleteVideo(editing, vid.id);
      for (let i = 0; i < selectedVideos.length; i++) await specialEventAPI.createVideo(editing, { videoId: selectedVideos[i].videoId, title: selectedVideos[i].title, order: i });

      queryClient.invalidateQueries(['special-event', editing]);
      navigate('/special-events');
    } else {
      createMutation.mutate({ form, trekIds: selectedTrekIds, stories: selectedStories, images: selectedImages, videos: selectedVideos });
    }
  };

  // TREKS
  const addTrek = (trekId) => {
    if (!selectedTrekIds.includes(trekId)) setSelectedTrekIds([...selectedTrekIds, trekId]);
  };
  const removeTrek = (trekId) => {
    setSelectedTrekIds(selectedTrekIds.filter((id) => id !== trekId));
  };
  const reorderTreks = useCallback((oldIndex, newIndex) => {
    const list = [...selectedTrekIds];
    const [moved] = list.splice(oldIndex, 1);
    list.splice(newIndex, 0, moved);
    setSelectedTrekIds(list);
  }, [selectedTrekIds]);

  // STORIES
  const addStory = (blogId) => {
    const blog = blogs.find(b => b.id === blogId);
    if (!blog) return;
    const entry = { blogId: blog.id, title: blog.title || '', slug: blog.slug || '' };
    if (!selectedStories.some((s) => s.blogId === blog.id)) setSelectedStories([...selectedStories, entry]);
  };
  const removeStory = (blogId) => {
    setSelectedStories(selectedStories.filter((s) => s.blogId !== blogId));
  };
  const reorderStories = useCallback((oldIndex, newIndex) => {
    const list = [...selectedStories];
    const [moved] = list.splice(oldIndex, 1);
    list.splice(newIndex, 0, moved);
    setSelectedStories(list);
  }, [selectedStories]);

  // VIDEOS
  const handleVideoChange = (index, field, value) => {
    const list = [...selectedVideos];
    list[index] = { ...list[index], [field]: value };
    setSelectedVideos(list);
  };
  const addVideo = () => setSelectedVideos([...selectedVideos, { videoId: '', title: '' }]);
  const removeVideo = (index) => setSelectedVideos(selectedVideos.filter((_, i) => i !== index));
  const reorderVideos = useCallback((oldIndex, newIndex) => {
    const list = [...selectedVideos];
    const [moved] = list.splice(oldIndex, 1);
    list.splice(newIndex, 0, moved);
    setSelectedVideos(list);
  }, [selectedVideos]);

  const availableTreks = useMemo(() => {
    let list = treks.filter((t) => !selectedTrekIds.includes(t.id));
    if (trekSearch) list = list.filter(t => t.name.toLowerCase().includes(trekSearch.toLowerCase()));
    return list;
  }, [treks, selectedTrekIds, trekSearch]);

  const availableStories = useMemo(() => {
    let list = blogs.filter((b) => !selectedStories.some((s) => s.blogId === b.id));
    if (storySearch) list = list.filter(b => (b.title || b.slug || b.id || '').toString().toLowerCase().includes(storySearch.toLowerCase()));
    return list;
  }, [blogs, selectedStories, storySearch]);
  
  const enabledTrekList = useMemo(() => selectedTrekIds.map(id => treks.find(t => t.id === id) || { id, name: 'Unknown Trek' }), [selectedTrekIds, treks]);

  const shareUrl = useMemo(() => specialEventPublicUrl(form.slug), [form.slug]);
  const isShareLive = form.status === 'active' && form.isActive !== false;

  const copyShareUrl = async (url) => {
    const target = String(url || '').trim();
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target);
      showToast({ message: 'Link copied to clipboard.', severity: 'success' });
    } catch {
      showToast({ message: 'Could not copy link.', severity: 'error' });
    }
  };

  return (
    <div className="module-page special-events-page">
      <AdminBackLink to="/special-events" label="Back to Special Events" />
      <PageHeader
        title={editing ? 'Edit Special Event' : 'Add Special Event'}
        subtitle={editing ? 'Update the details below.' : 'Create a new special event collection.'}
      />

      <div className="module-card">
        <form className="special-events-form" onSubmit={onSubmit}>
          {/* SECTION: BASIC INFO */}
          <h3 className="module-section-title" style={{ marginTop: 0 }}>Basic Information</h3>
          <div className="special-events-form-row special-events-form-row--full">
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </div>
          <div className="special-events-form-row">
            <div className="special-events-field">
              <label>Tagline</label>
              <input value={form.tagline} onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} />
            </div>
            <div className="special-events-field">
              <label>Slug</label>
              <input
                value={form.slug}
                onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                required
                disabled={!canEditSlugs}
              />
            </div>
          </div>
          
          {form.slug ? (
            <div className="special-events-share-box" style={{ marginTop: '16px', marginBottom: '16px' }}>
              <label className="special-events-share-label">Shareable public link</label>
              <div className="special-events-share-row">
                <input type="text" readOnly value={shareUrl} className="special-events-share-input" onFocus={(e) => e.target.select()} />
                <button type="button" className="special-events-btn-secondary special-events-share-btn" onClick={() => copyShareUrl(shareUrl)} title="Copy link">
                  <Copy size={16} /> Copy
                </button>
                {isShareLive ? (
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="special-events-btn-secondary special-events-share-btn">
                    <ExternalLink size={16} /> Open
                  </a>
                ) : null}
              </div>
              <p className="special-events-share-hint">
                {isShareLive ? 'Share this link — visitors see enabled treks and stories for this collection.' : 'Set status to Active to publish this link (draft/inactive links return “not found”).'}
              </p>
            </div>
          ) : null}

          <div className="special-events-form-row">
            <div className="special-events-field" style={{ maxWidth: '300px' }}>
              <label>Status</label>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* SECTION: BENTO GRID IMAGES */}
          <hr className="module-divider" />
          <h3 className="module-section-title">Photo Grid (Bento Grid)</h3>
          <p className="field-hint" style={{ marginBottom: '16px' }}>Upload and drag to reorder. These will be displayed in the Bento Grid.</p>
          <div className="module-form-row">
            <div className="module-form-group full-width">
              <MultiImageUpload
                value={selectedImages}
                onChange={setSelectedImages}
                uploadImage={async (file) => {
                  const res = await adminAPI.uploadImage(file);
                  return res.data?.data?.url || res.data?.url || '';
                }}
                maxImages={12}
              />
            </div>
          </div>

          {/* SECTION: ENABLED TREKS */}
          <hr className="module-divider" />
          <h3 className="module-section-title">Treks</h3>
          <p className="field-hint" style={{ marginBottom: '16px' }}>Drag and drop to reorder the treks displayed in the Trek grid.</p>
          
          <div className="special-events-two-col">
            <div className="special-events-panel">
              <h4 className="special-events-panel-title">Enabled Treks</h4>
              <div className="special-events-enabled-box">
                {enabledTrekList.length === 0 ? (
                  <span className="special-events-empty-hint">No treks enabled</span>
                ) : (
                  <SortableList
                    items={enabledTrekList}
                    onReorder={reorderTreks}
                    renderItem={(t) => (
                      <div key={t.id} className="special-events-enabled-item" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="drag-handle" style={{ cursor: 'grab', color: '#999', display: 'flex' }}><GripVertical size={16} /></div>
                        <span style={{ flex: 1 }}>{t.name}</span>
                        <button type="button" className="special-events-remove-btn" onClick={() => removeTrek(t.id)} title="Remove"><X size={14} /></button>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
            <div className="special-events-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 className="special-events-panel-title" style={{ margin: 0 }}>Available Treks</h4>
                <input
                  type="text"
                  className="module-filter-input"
                  placeholder="Search treks..."
                  value={trekSearch}
                  onChange={(e) => setTrekSearch(e.target.value)}
                  style={{ width: '220px', margin: 0 }}
                />
              </div>
              <div className="special-events-available-list">
                {availableTreks.map((t) => (
                  <div key={t.id} className="special-events-available-item">
                    <span>{t.name}</span>
                    <button type="button" className="special-events-add-btn" onClick={() => addTrek(t.id)} title="Add">
                      <Plus size={16} />
                    </button>
                  </div>
                ))}
                {availableTreks.length === 0 && <span className="special-events-empty-hint">No more treks to add</span>}
              </div>
            </div>
          </div>

          {/* SECTION: YOUTUBE VIDEOS */}
          <hr className="module-divider" />
          <h3 className="module-section-title">YouTube Videos Grid</h3>
          <p className="field-hint" style={{ marginBottom: '16px' }}>Add YouTube Video IDs (e.g. <code>NJ8PPzIbA5s</code>) and drag to reorder.</p>
          
          <div className="module-form-group full-width" style={{ maxWidth: '700px' }}>
            {selectedVideos.length > 0 && (
              <table className="module-table" style={{ width: '100%', marginBottom: '8px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Video ID or YouTube Link</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <SortableList
                  container="tbody"
                  items={selectedVideos}
                  onReorder={reorderVideos}
                  renderItem={(vid, idx) => (
                    <tr key={idx} style={{ background: '#fff' }}>
                      <td style={{ textAlign: 'center' }}>
                        <div className="drag-handle" style={{ cursor: 'grab', color: '#999', display: 'inline-flex' }}><GripVertical size={16} /></div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="module-input"
                          placeholder="e.g. NJ8PPzIbA5s"
                          value={vid.videoId}
                          onChange={(e) => handleVideoChange(idx, 'videoId', e.target.value)}
                          style={{ margin: 0, width: '100%' }}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" onClick={() => removeVideo(idx)} className="module-btn-icon remove" style={{ margin: '0 auto' }}><X size={16}/></button>
                      </td>
                    </tr>
                  )}
                />
              </table>
            )}
            <button type="button" onClick={addVideo} className="special-events-btn-secondary" style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Plus size={16} /> Add Video
            </button>
          </div>

          {/* SECTION: ENABLED STORIES */}
          <hr className="module-divider" />
          <h3 className="module-section-title">Stories</h3>
          <p className="field-hint" style={{ marginBottom: '16px' }}>Drag and drop to reorder the stories displayed in the Stories grid.</p>
          
          <div className="special-events-two-col">
            <div className="special-events-panel">
              <h4 className="special-events-panel-title">Enabled Stories</h4>
              <div className="special-events-enabled-box">
                {selectedStories.length === 0 ? (
                  <span className="special-events-empty-hint">No stories enabled</span>
                ) : (
                  <SortableList
                    items={selectedStories}
                    onReorder={reorderStories}
                    renderItem={(s) => (
                      <div key={s.blogId || s.id} className="special-events-enabled-item" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="drag-handle" style={{ cursor: 'grab', color: '#999', display: 'flex' }}><GripVertical size={16} /></div>
                        <span style={{ flex: 1 }}>{s.title || s.slug || 'Story'}</span>
                        <button type="button" className="special-events-remove-btn" onClick={() => removeStory(s.blogId)} title="Remove"><X size={14} /></button>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
            <div className="special-events-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 className="special-events-panel-title" style={{ margin: 0 }}>Available Stories</h4>
                <input
                  type="text"
                  className="module-filter-input"
                  placeholder="Search stories..."
                  value={storySearch}
                  onChange={(e) => setStorySearch(e.target.value)}
                  style={{ width: '220px', margin: 0 }}
                />
              </div>
              <div className="special-events-available-list">
                {availableStories.map((b) => (
                  <div key={b.id} className="special-events-available-item">
                    <span>{b.title || b.slug || b.id}</span>
                    <button type="button" className="special-events-add-btn" onClick={() => addStory(b.id)} title="Add">
                      <Plus size={16} />
                    </button>
                  </div>
                ))}
                {availableStories.length === 0 && <span className="special-events-empty-hint">No more stories to add</span>}
              </div>
            </div>
          </div>

          <hr className="module-divider" style={{ marginTop: '32px' }} />

          <div className="special-events-form-actions">
            <button type="submit" className="special-events-btn-primary special-events-btn-save">
              <Save size={18} />
              {editing ? 'Update Special Event' : 'Create Special Event'}
            </button>
            <button type="button" className="special-events-btn-secondary" onClick={() => navigate('/special-events')}>
              Cancel
            </button>
            {editing && (
              <button
                type="button"
                className="special-events-btn-danger"
                onClick={() => { if (window.confirm('Delete this special event?')) deleteMutation.mutate(editing); }}
              >
                Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecialEventForm;
